import React, { useState, useEffect } from 'react';
import { Card } from '../ui/Card';
import { Button } from '../ui/Button';
import { database } from '../../config/backend';
import { ref, get as rtdbGet, set, child } from '../../services/phpDataAdapter';
import './Assessment.css';

interface AnamnesisData {
  dietaryHistory?: string;
  dailyRoutine?: string;
  mealTimes?: string;
  sleepSchedule?: string;
  foodPreferences?: string;
  foodAversions?: string;
  dietaryRestrictions?: string;
  medicalHistory?: string;
  supplements?: string;
  medications?: string;
  allergies?: string;
  intolerances?: string;
  physicalActivity?: string;
  waterIntake?: string;
  alcoholConsumption?: string;
  smoking?: string;
  notes?: string;
}

interface AnamnesisFormProps {
  patientId: string;
  onSave?: () => void;
}

export const AnamnesisForm: React.FC<AnamnesisFormProps> = ({ patientId, onSave }) => {
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState<AnamnesisData>({});

  useEffect(() => {
    if (patientId) {
      loadAnamnesis();
    }
  }, [patientId]);

  const loadAnamnesis = async () => {
    if (!patientId) return;
    setLoading(true);
    try {
      const snap = await rtdbGet(child(ref(database), `users/${patientId}/assessment/anamnesis`));
      if (snap.exists()) {
        setFormData(snap.val() as AnamnesisData);
      }
    } catch (error) {
      console.error('Erro ao carregar anamnese:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    if (!patientId) return;
    setSaving(true);
    try {
      await set(ref(database, `users/${patientId}/assessment/anamnesis`), {
        ...formData,
        updatedAt: new Date().toISOString(),
      });
      if (onSave) onSave();
      alert('Anamnese salva com sucesso!');
    } catch (error) {
      console.error('Erro ao salvar anamnese:', error);
      alert('Erro ao salvar anamnese');
    } finally {
      setSaving(false);
    }
  };

  const updateField = (field: keyof AnamnesisData, value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  if (loading) {
    return (
      <Card title="Anamnese Nutricional" subtitle="Carregando...">
        <p>Carregando dados da anamnese...</p>
      </Card>
    );
  }

  return (
    <Card
      title="Anamnese Nutricional"
      subtitle="Histórico alimentar, rotina e preferências do paciente"
      actions={
        <Button onClick={handleSave} disabled={saving}>
          {saving ? 'Salvando...' : 'Salvar Anamnese'}
        </Button>
      }
    >
      <div className="ns-anamnesis-form">
        <div className="ns-form-section">
          <h3>Histórico Alimentar</h3>
          <label className="ns-field">
            <span>Descreva o histórico alimentar do paciente</span>
            <textarea
              className="ns-textarea"
              rows={4}
              value={formData.dietaryHistory || ''}
              onChange={(e) => updateField('dietaryHistory', e.target.value)}
              placeholder="Ex: Consome 3 refeições principais, raramente faz lanches, gosta de alimentos doces..."
            />
          </label>

          <label className="ns-field">
            <span>Horários das refeições</span>
            <input
              type="text"
              className="ns-input"
              value={formData.mealTimes || ''}
              onChange={(e) => updateField('mealTimes', e.target.value)}
              placeholder="Ex: Café 7h, Almoço 12h30, Jantar 19h"
            />
          </label>
        </div>

        <div className="ns-form-section">
          <h3>Rotina Diária</h3>
          <label className="ns-field">
            <span>Descreva a rotina diária</span>
            <textarea
              className="ns-textarea"
              rows={3}
              value={formData.dailyRoutine || ''}
              onChange={(e) => updateField('dailyRoutine', e.target.value)}
              placeholder="Ex: Acorda 6h, trabalha das 8h às 17h, treina às 18h..."
            />
          </label>

          <label className="ns-field">
            <span>Horário de sono</span>
            <input
              type="text"
              className="ns-input"
              value={formData.sleepSchedule || ''}
              onChange={(e) => updateField('sleepSchedule', e.target.value)}
              placeholder="Ex: Dorme 23h, acorda 6h (7h de sono)"
            />
          </label>
        </div>

        <div className="ns-form-section">
          <h3>Preferências e Aversões</h3>
          <label className="ns-field">
            <span>Preferências alimentares</span>
            <textarea
              className="ns-textarea"
              rows={3}
              value={formData.foodPreferences || ''}
              onChange={(e) => updateField('foodPreferences', e.target.value)}
              placeholder="Ex: Gosta de frutas, vegetais, peixes, prefere alimentos doces..."
            />
          </label>

          <label className="ns-field">
            <span>Aversões alimentares</span>
            <textarea
              className="ns-textarea"
              rows={3}
              value={formData.foodAversions || ''}
              onChange={(e) => updateField('foodAversions', e.target.value)}
              placeholder="Ex: Não gosta de vegetais amargos, evita alimentos muito picantes..."
            />
          </label>

          <label className="ns-field">
            <span>Restrições dietéticas</span>
            <input
              type="text"
              className="ns-input"
              value={formData.dietaryRestrictions || ''}
              onChange={(e) => updateField('dietaryRestrictions', e.target.value)}
              placeholder="Ex: Vegetariano, sem glúten, sem lactose..."
            />
          </label>
        </div>

        <div className="ns-form-section">
          <h3>Histórico Médico</h3>
          <label className="ns-field">
            <span>Histórico de doenças e condições</span>
            <textarea
              className="ns-textarea"
              rows={3}
              value={formData.medicalHistory || ''}
              onChange={(e) => updateField('medicalHistory', e.target.value)}
              placeholder="Ex: Diabetes tipo 2, hipertensão, hipotireoidismo..."
            />
          </label>

          <label className="ns-field">
            <span>Alergias</span>
            <input
              type="text"
              className="ns-input"
              value={formData.allergies || ''}
              onChange={(e) => updateField('allergies', e.target.value)}
              placeholder="Ex: Alergia a amendoim, camarão..."
            />
          </label>

          <label className="ns-field">
            <span>Intolerâncias</span>
            <input
              type="text"
              className="ns-input"
              value={formData.intolerances || ''}
              onChange={(e) => updateField('intolerances', e.target.value)}
              placeholder="Ex: Intolerância à lactose, frutose..."
            />
          </label>

          <label className="ns-field">
            <span>Medicamentos em uso</span>
            <textarea
              className="ns-textarea"
              rows={2}
              value={formData.medications || ''}
              onChange={(e) => updateField('medications', e.target.value)}
              placeholder="Ex: Metformina 500mg 2x/dia, Levotiroxina 75mcg..."
            />
          </label>

          <label className="ns-field">
            <span>Suplementos</span>
            <textarea
              className="ns-textarea"
              rows={2}
              value={formData.supplements || ''}
              onChange={(e) => updateField('supplements', e.target.value)}
              placeholder="Ex: Whey protein, creatina, vitamina D..."
            />
          </label>
        </div>

        <div className="ns-form-section">
          <h3>Estilo de Vida</h3>
          <label className="ns-field">
            <span>Atividade física</span>
            <textarea
              className="ns-textarea"
              rows={2}
              value={formData.physicalActivity || ''}
              onChange={(e) => updateField('physicalActivity', e.target.value)}
              placeholder="Ex: Academia 3x/semana, corrida 2x/semana..."
            />
          </label>

          <div className="ns-form-row">
            <label className="ns-field">
              <span>Ingestão de água (L/dia)</span>
              <input
                type="text"
                className="ns-input"
                value={formData.waterIntake || ''}
                onChange={(e) => updateField('waterIntake', e.target.value)}
                placeholder="Ex: 2L, 2.5L..."
              />
            </label>

            <label className="ns-field">
              <span>Consumo de álcool</span>
              <input
                type="text"
                className="ns-input"
                value={formData.alcoholConsumption || ''}
                onChange={(e) => updateField('alcoholConsumption', e.target.value)}
                placeholder="Ex: 2-3x/semana, socialmente..."
              />
            </label>
          </div>

          <label className="ns-field">
            <span>Tabagismo</span>
            <input
              type="text"
              className="ns-input"
              value={formData.smoking || ''}
              onChange={(e) => updateField('smoking', e.target.value)}
              placeholder="Ex: Não fuma, ex-fumante há 2 anos..."
            />
          </label>
        </div>

        <div className="ns-form-section">
          <h3>Observações Adicionais</h3>
          <label className="ns-field">
            <span>Notas e observações</span>
            <textarea
              className="ns-textarea"
              rows={4}
              value={formData.notes || ''}
              onChange={(e) => updateField('notes', e.target.value)}
              placeholder="Outras informações relevantes sobre o paciente..."
            />
          </label>
        </div>
      </div>
    </Card>
  );
};

